/**
 * Compare Panel Component
 *
 * Allows users to compare the current configuration with:
 * - A previous snapshot from history
 * - An imported configuration file
 */

import { useState, useRef } from 'react';
import {
  ArrowLeftRight,
  History,
  Upload,
  X,
  FileJson,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { Button, Card } from '../ui';
import { ConfigComparison } from './ConfigComparison';
import { useConfigurator } from '../../hooks/useConfigurator';
import { DatacenterConfig } from '../../types/configurator';
import { clsx } from 'clsx';

interface ComparePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type CompareSource = 'history' | 'file' | null;

export function ComparePanel({ isOpen, onClose }: ComparePanelProps) {
  const { config, history, historyIndex } = useConfigurator();
  const [compareSource, setCompareSource] = useState<CompareSource>(null);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<number | null>(null);
  const [importedConfig, setImportedConfig] = useState<DatacenterConfig | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);

        // Basic validation
        if (!parsed.network || !parsed.nodes || !parsed.security) {
          throw new Error('Invalid configuration format');
        }

        setImportedConfig(parsed as DatacenterConfig);
        setImportError(null);
      } catch (error) {
        setImportError(
          error instanceof Error
            ? error.message
            : 'Failed to parse configuration file'
        );
        setImportedConfig(null);
      }
    };
    reader.readAsText(file);
  };

  const handleReset = () => {
    setCompareSource(null);
    setSelectedHistoryIndex(null);
    setImportedConfig(null);
    setImportError(null);
  };

  const getCompareConfig = (): DatacenterConfig | null => {
    if (compareSource === 'history' && selectedHistoryIndex !== null) {
      return history[selectedHistoryIndex]?.config || null;
    }
    if (compareSource === 'file' && importedConfig) {
      return importedConfig;
    }
    return null;
  };

  const getCompareLabel = (): string => {
    if (compareSource === 'history' && selectedHistoryIndex !== null) {
      const snapshot = history[selectedHistoryIndex];
      return snapshot?.label || `Snapshot ${selectedHistoryIndex + 1}`;
    }
    if (compareSource === 'file' && importedConfig) {
      return importedConfig.name || 'Imported Config';
    }
    return 'Compare Config';
  };

  const compareConfig = getCompareConfig();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ArrowLeftRight className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Compare Configurations</h2>
              <p className="text-sm text-gray-500">
                Compare current config with a previous version or imported file
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!compareConfig ? (
            <div className="space-y-6">
              {/* Source Selection */}
              <div className="grid grid-cols-2 gap-4">
                {/* History Source */}
                <Card
                  selected={compareSource === 'history'}
                  onClick={() => setCompareSource('history')}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-3 p-4">
                    <div
                      className={clsx(
                        'p-3 rounded-lg',
                        compareSource === 'history'
                          ? 'bg-indigo-100 text-indigo-600'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      <History className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">From History</h3>
                      <p className="text-sm text-gray-500">
                        Compare with a previous snapshot
                      </p>
                    </div>
                  </div>
                </Card>

                {/* File Source */}
                <Card
                  selected={compareSource === 'file'}
                  onClick={() => setCompareSource('file')}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-3 p-4">
                    <div
                      className={clsx(
                        'p-3 rounded-lg',
                        compareSource === 'file'
                          ? 'bg-indigo-100 text-indigo-600'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      <Upload className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Import File</h3>
                      <p className="text-sm text-gray-500">
                        Compare with an exported config
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* History Selection */}
              {compareSource === 'history' && (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700">Select a snapshot to compare</h3>
                  {history.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No history snapshots available yet.</p>
                      <p className="text-sm">Make some changes to create history.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {history.map((snapshot, idx) => (
                        <button
                          key={snapshot.id}
                          onClick={() => setSelectedHistoryIndex(idx)}
                          className={clsx(
                            'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left',
                            selectedHistoryIndex === idx
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          )}
                        >
                          <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {snapshot.label || `Snapshot ${idx + 1}`}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(snapshot.timestamp).toLocaleString()}
                            </p>
                          </div>
                          {idx === historyIndex && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              Current
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedHistoryIndex !== null && (
                    <Button
                      onClick={() => {}}
                      className="w-full"
                      disabled={selectedHistoryIndex === historyIndex}
                    >
                      Compare with Snapshot {selectedHistoryIndex + 1}
                    </Button>
                  )}
                </div>
              )}

              {/* File Import */}
              {compareSource === 'file' && (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700">Import a configuration file</h3>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileImport}
                    className="hidden"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={clsx(
                      'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                      importedConfig
                        ? 'border-green-300 bg-green-50'
                        : importError
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    )}
                  >
                    {importedConfig ? (
                      <>
                        <FileJson className="h-8 w-8 mx-auto mb-2 text-green-600" />
                        <p className="font-medium text-green-700">
                          {importedConfig.name || 'Imported Configuration'}
                        </p>
                        <p className="text-sm text-green-600">
                          {importedConfig.nodes.length} nodes, {importedConfig.scenario} scenario
                        </p>
                      </>
                    ) : importError ? (
                      <>
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-500" />
                        <p className="font-medium text-red-700">Import Failed</p>
                        <p className="text-sm text-red-600">{importError}</p>
                        <p className="text-xs text-gray-500 mt-2">Click to try again</p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="font-medium text-gray-700">
                          Click to upload a configuration file
                        </p>
                        <p className="text-sm text-gray-500">
                          Supports JSON files exported from this configurator
                        </p>
                      </>
                    )}
                  </div>
                  {importedConfig && (
                    <Button onClick={() => {}} className="w-full">
                      Compare with Imported Config
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <ConfigComparison
              configA={config}
              configB={compareConfig}
              labelA="Current"
              labelB={getCompareLabel()}
              onClose={handleReset}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
          {compareConfig && (
            <Button variant="outline" onClick={handleReset}>
              Choose Different Source
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
